import { generatePath, useMatch, useNavigate } from 'react-router-dom';

import { useDeleteModal } from '@components/generic-modals';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconDocument } from '@components/icons/document';
import { IconDrag } from '@components/icons/drag';
import { IconEdit } from '@components/icons/edit';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconTrash } from '@components/icons/trash';
import { RelativeSpinner } from '@components/loading-spinner';
import { NavGroupOfGroupsCollapse } from '@components/nav-group-collapse/nav-group-collapse';
import { useIsSidebarActive } from '@components/sidebar';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';
import { Color } from '@styles/theme';
import { storage } from '@utils/browser';

import { useCurrentCompany, useUserOption } from 'api/authentication/hooks';
import { useDeleteWorkspace, useWorkspacesOrdered } from 'api/workspaces/hooks';
import { usePermissions } from 'contexts/permissions-context';
import { routePaths } from 'ui/authenticated/navigation';

import { useCurrentWorkspace } from '../helpers';
import { useWorkspaceFormSidebar } from '../pages/workspaces/sidebars/form';

const CREATE_SIDEBAR_ID = 'create-sidebar';
const expandedStore = createSelectionStore({ persistKey: 'workspaces-nav-state' });

export const Workspaces = () => {
  const workspaces = useWorkspacesOrdered();
  const { update } = useUserOption('navbarWorkspacesOrder', []);
  const currentWorkspace = useCurrentWorkspace();
  const currentCompany = useCurrentCompany();
  const { t } = useScopedTranslate(locale);
  const showFormSidebar = useWorkspaceFormSidebar();
  const isCreateSidebarActive = useIsSidebarActive(CREATE_SIDEBAR_ID);
  const showDeleteModal = useDeleteModal();
  const deleteMutation = useDeleteWorkspace();
  const params = useMatch('/workspaces/:workspaceId/*')?.params;
  const workspaceId = params?.workspaceId ?? '';
  const navigate = useNavigate();
  const { canManageWorkspaces } = usePermissions();

  const handleDelete = async (workspaceId: string) => {
    if (currentCompany) {
      storage.remove('current-workspace-' + currentCompany.shortId);
    }
    await deleteMutation.mutateAsync(workspaceId);
    if (currentWorkspace.shortId === workspaceId) {
      const firstWorkspace = workspaces?.find((workspace) => workspace.shortId !== workspaceId);
      if (firstWorkspace) {
        navigate(
          generatePath(routePaths.workspaceJourneyList, {
            workspaceId: firstWorkspace.shortId,
          })
        );
      } else {
        navigate(routePaths.travellersList);
      }
    }
  };

  if (!workspaces || !currentCompany) {
    return <RelativeSpinner />;
  }
  return (
    <NavGroupOfGroupsCollapse
      id="workspaces"
      label={t('workspaces')}
      toggleGroup={expandedStore.toggle}
      expandedSelectionStore={expandedStore}
      addSidebar={
        canManageWorkspaces && !isCreateSidebarActive
          ? () => showFormSidebar({}, { id: CREATE_SIDEBAR_ID })
          : undefined
      }
      onDragEnd={update.mutate}
      groups={workspaces.map((workspace) => {
        const workspaceProp = {
          workspaceId: workspace.shortId,
        };
        const setCurrentWorkspace = () => {
          storage.set('current-workspace-' + currentCompany.shortId, workspace.shortId);
        };
        const options = canManageWorkspaces
          ? [
              {
                label: t('edit'),
                icon: <IconEdit />,
                onClick: () => showFormSidebar(workspaceProp),
              },
              {
                label: t('delete'),
                icon: <IconTrash />,
                isDanger: true,
                onClick: () =>
                  showDeleteModal({
                    title: t('deleteWorkspace'),
                    description: t('deleteWorkspaceDescription'),
                    buttonText: t('delete'),
                    onConfirm: () => handleDelete(workspace.shortId),
                    hasUserConfirmation: true,
                  }),
              },
            ]
          : [];
        return {
          id: workspace.shortId,
          label: workspace.title,
          isPrivate: workspace.isPrivate,
          isActive: workspace.shortId === workspaceId,
          icon: <IconRenderer iconName={workspace.icon} color={workspace.color} />,
          options,
          links: [
            {
              href: generatePath(routePaths.workspaceJourneyList, workspaceProp),
              iconLeft: <IconJourneyItem size={16} />,
              label: t('journeys'),
              onClick: setCurrentWorkspace,
            },
            {
              href: generatePath(routePaths.workspaceDocuments, workspaceProp),
              iconLeft: <IconDocument size={16} />,
              label: t('documents'),
              onClick: setCurrentWorkspace,
            },
          ],
        };
      })}
    />
  );
};

const IconRenderer = ({
  iconName,
  color,
}: {
  iconName: keyof typeof ICON_CHOOSER_ICONS;
  color: Color;
}) => {
  const IconComponent = ICON_CHOOSER_ICONS[iconName] ?? ICON_CHOOSER_ICONS.IconWorkspace;
  return (
    <IconFancyContainer
      size="sm"
      icon={<IconComponent size={16} color={color} />}
      hoverIcon={<IconDrag size={16} color="white100" />}
      variant={COLOR_TO_VARIANT[color]}
    />
  );
};

const locale = {
  en: {
    journeys: 'Journeys',
    documents: 'Documents',
    workspaces: 'Workspaces',
    edit: 'Edit',
    delete: 'Delete',
    deleteWorkspace: 'Delete Workspace',
    deleteWorkspaceDescription:
      'Are you sure you want to delete this workspace? It will permanently delete all journeys, forms and documents.',
  },
};
